import { Link } from 'react-router-dom'
import { TrendingUp, Users, Award, Globe, Smartphone, ShoppingCart, ArrowRight, CheckCircle, Search, TrendingUp as TrendingUpIcon, Store, Video, GraduationCap, Calculator, BookOpen, Clock, MessageCircle, QrCode, Link2 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { motion } from 'framer-motion'
import { useLanguage } from '../contexts/LanguageContext'
import { useMemo } from 'react'
import translations from '../locales/translations'
import { getImageDimensions } from '../utils/imageDimensions'

type ServiceCard = {
  icon: LucideIcon
  title: string
  description: string
  price?: string
  duration?: string
  link: string
  features: string[]
  color: string
  borderColor?: string
  badge?: string
}

const Services = () => {
  const { language } = useLanguage()
  
  const pageContent = useMemo(() => {
    return (translations as any)[language]
  }, [language])
  
  const renderLogo = (logo: string, key: string, order: number) => {
    const dimensions = getImageDimensions(logo)
    return (
      <div key={key} className="flex-shrink-0 bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
        <img
          src={logo}
          alt={`Referans ${order}`}
          loading="lazy"
          decoding="async"
          width={dimensions?.width}
          height={dimensions?.height}
          className="h-8 sm:h-12 max-w-[100px] sm:max-w-[140px] object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
        />
      </div>
    )
  }
  
  const services = useMemo<ServiceCard[]>(() => {
    const homeServices = pageContent.home.services
    return [
      {
        icon: TrendingUp,
        title: homeServices.googleAds.title,
        description: homeServices.googleAds.description,
        price: homeServices.googleAds.price,
        duration: homeServices.googleAds.duration,
        link: '/services/google-ads',
        features: homeServices.googleAds.features ?? ['Google Ads Campaign Management', 'Keyword Optimization', 'ROI Tracking & Reporting'],
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Users,
        title: homeServices.metaAds.title,
        description: homeServices.metaAds.description,
        price: homeServices.metaAds.price,
        duration: homeServices.metaAds.duration,
        link: '/services/meta-ads',
        features: homeServices.metaAds.features ?? ['Facebook & Instagram Ads', 'Audience Optimization', 'Visual & Video Design'],
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Award,
        title: homeServices.socialMedia.title,
        description: homeServices.socialMedia.description,
        price: homeServices.socialMedia.price,
        duration: homeServices.socialMedia.duration,
        link: '/services/social-media',
        features: homeServices.socialMedia.features ?? ['Content Creation & Planning', 'Community Management', 'Engagement Analysis'],
        color: 'from-pink-500 to-pink-600'
      },
      {
        icon: Globe,
        title: homeServices.webDesign.title,
        description: homeServices.webDesign.description,
        price: homeServices.webDesign.price,
        duration: homeServices.webDesign.duration,
        link: '/services/web-design',
        features: homeServices.webDesign.features ?? ['Modern & Responsive Design', 'SEO Optimization', 'Speed & Performance'],
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Search,
        title: homeServices.seoGeo.title,
        description: homeServices.seoGeo.description,
        price: homeServices.seoGeo.price,
        duration: homeServices.seoGeo.duration,
        link: '/services/seo-geo',
        features: homeServices.seoGeo.features ?? ['Keyword Research & Analysis', 'Technical SEO Optimization', 'Google Maps Optimization'],
        color: 'from-teal-500 to-teal-600'
      },
      {
        icon: Smartphone,
        title: homeServices.appDevelopment.title,
        description: homeServices.appDevelopment.description,
        price: homeServices.appDevelopment.price,
        duration: homeServices.appDevelopment.duration,
        link: '/services/app-development',
        features: homeServices.appDevelopment.features ?? ['iOS & Android Development', 'API Integration', 'Maintenance & Support'],
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: ShoppingCart,
        title: pageContent.services.marketplace,
        description: homeServices.marketplace.description,
        price: homeServices.marketplace.price,
        duration: homeServices.marketplace.duration,
        link: '/services/marketplace',
        features: homeServices.marketplace.features ?? ['Marketplace Strategy', 'Ads Optimization', 'Content & Creative'],
        color: 'from-indigo-500 to-indigo-600'
      },
      {
        icon: MessageCircle,
        title: language === 'tr' ? 'WhatsApp Link Oluşturucu' : language === 'en' ? 'WhatsApp Link Generator' : 'Генератор ссылок WhatsApp',
        description: language === 'tr' ? 'Telefon numaranız için özel WhatsApp mesaj linkleri oluşturun' : language === 'en' ? 'Create custom WhatsApp message links for your phone number' : 'Создавайте пользовательские ссылки для сообщений WhatsApp',
        link: '/whatsapp-link-generator',
        features: language === 'tr' 
          ? ['Otomatik format temizleme', 'Önceden yazılmış mesaj desteği', 'Hızlı link oluşturma']
          : language === 'en'
          ? ['Automatic format cleaning', 'Pre-written message support', 'Quick link generation']
          : ['Автоматическая очистка формата', 'Поддержка предварительно написанных сообщений', 'Быстрое создание ссылок'],
        color: 'from-green-500 to-green-600'
      },
      {
        icon: QrCode,
        title: language === 'tr' ? 'QR Kod Oluşturucu' : language === 'en' ? 'QR Code Generator' : 'Генератор QR-кодов',
        description: language === 'tr' ? 'Herhangi bir metin veya link için QR kod oluşturun' : language === 'en' ? 'Create a QR code for any text or link' : 'Создайте QR-код для любого текста или ссылки',
        link: '/qr-code-generator',
        features: language === 'tr'
          ? ['Özelleştirilebilir boyut', 'Hata düzeltme seviyesi', 'PNG indirme']
          : language === 'en'
          ? ['Customizable size', 'Error correction level', 'PNG download']
          : ['Настраиваемый размер', 'Уровень коррекции ошибок', 'Скачивание PNG'],
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Link2,
        title: language === 'tr' ? 'UTM Link Oluşturucu' : language === 'en' ? 'UTM Link Generator' : 'Генератор UTM-ссылок',
        description: language === 'tr' ? 'Kampanya takibi için UTM parametreli linkler oluşturun' : language === 'en' ? 'Create UTM parameterized links for campaign tracking' : 'Создавайте ссылки с UTM-параметрами для отслеживания кампаний',
        link: '/utm-link-generator',
        features: language === 'tr'
          ? ['5 UTM parametresi', 'Kampanya takibi', 'Google Analytics entegrasyonu']
          : language === 'en'
          ? ['5 UTM parameters', 'Campaign tracking', 'Google Analytics integration']
          : ['5 UTM-параметров', 'Отслеживание кампаний', 'Интеграция с Google Analytics'],
        color: 'from-emerald-500 to-emerald-600'
      }
    ]
  }, [pageContent, language])

  return (
    <div className="pt-16 bg-[#f9f9f9] relative">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none"></div>
      
      {/* Hero Section */}
      <section className="py-24 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block text-[13px] font-medium text-[#666] bg-white px-4 py-1.5 rounded-full border border-gray-200 mb-6">
              {language === 'tr' ? 'Hizmetlerimiz' : language === 'en' ? 'Our Services' : 'Наши услуги'}
            </span>
            <h1 className="text-[28px] md:text-[44px] font-bold text-[#111] leading-tight mb-6">
              {language === 'tr' ? 'Dijital Pazarlama Hizmetlerimiz' : language === 'en' ? 'Digital Marketing Services' : 'Услуги цифрового маркетинга'}
            </h1>
            <p className="text-lg md:text-xl text-[#555] max-w-3xl mx-auto mb-8 leading-relaxed">
              {language === 'tr' ? 'Markanızın dijital dünyada başarılı olması için ihtiyaç duyduğu tüm hizmetleri tek çatı altında sunuyoruz.' : language === 'en' ? 'We offer all the services your brand needs to succeed in the digital world under one roof.' : 'Мы предлагаем все услуги, необходимые вашему бренду для успеха в цифровом мире, под одной крышей.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 relative">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link
                  to={service.link}
                  className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-transparent hover:shadow-2xl transition-all duration-300 h-full flex flex-col relative overflow-hidden"
                >
                  {/* Gradient background on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                  
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-6">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg group-hover:shadow-xl`}>
                        <service.icon className="w-7 h-7 text-white" />
                      </div>
                  </div>
                    
                    <h3 className="text-2xl font-bold text-[#111] mb-3 group-hover:text-gray-900 transition-colors">
                      {service.title}
                    </h3>
                    
                    <p className="text-[#555] leading-relaxed mb-6 flex-grow">
                  {service.description}
                </p>
                
                    {/* Features List */}
                    <div className="space-y-3 mb-6 border-t border-gray-100 pt-6">
                  {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${service.color} opacity-10 group-hover:opacity-20 flex items-center justify-center flex-shrink-0 transition-opacity`}>
                            <CheckCircle className="w-3.5 h-3.5 text-gray-700" />
                          </div>
                          <span className="text-sm text-[#555]">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-end pt-4 border-t border-gray-100">
                      <div className="flex items-center text-gray-700 font-semibold group-hover:gap-2 transition-all">
                        {language === 'tr' ? 'Detaylar' : language === 'en' ? 'Details' : 'Подробнее'}
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:ml-0 group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* References Section */}
      <section className="bg-white py-24 relative border-t border-gray-200">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="flex flex-col items-center gap-5"
          >
            {/* Client Logos Grid - Two Rows */}
            <div className="w-full overflow-hidden py-4 relative">
              {/* Top Row - Slides Right */}
              <div className="overflow-hidden mb-4 relative">
                {/* Fade gradients */}
                <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
                <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
                <div className="flex animate-marquee-right gap-4">
                  {/* First set */}
                  {[
                    '/images/brands/ref1.webp',
                    '/images/brands/ref2.webp',
                    '/images/brands/ref3.webp',
                    '/images/brands/ref4.webp',
                    '/images/brands/ref8.webp',
                    '/images/brands/ref6.webp',
                    '/images/brands/ref7.webp',
                  ].map((logo, index) => renderLogo(logo, `top-${index}`, index + 1))}
                  {/* Duplicate set for seamless loop */}
                  {[
                    '/images/brands/ref1.webp',
                    '/images/brands/ref2.webp',
                    '/images/brands/ref3.webp',
                    '/images/brands/ref4.webp',
                    '/images/brands/ref8.webp',
                    '/images/brands/ref6.webp',
                    '/images/brands/ref7.webp',
                  ].map((logo, index) => renderLogo(logo, `top-dup-${index}`, index + 1))}
                </div>
              </div>

              {/* Bottom Row - Slides Left */}
              <div className="overflow-hidden relative">
                {/* Fade gradients */}
                <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
                <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
                <div className="flex animate-marquee-left gap-4">
                  {/* First set */}
                  {[
                    '/images/brands/veela1.webp',
                    '/images/brands/daphnesandals.webp',
                    '/images/brands/ref8.webp',
                    '/images/brands/ref1.webp',
                    '/images/brands/ref2.webp',
                    '/images/brands/ref3.webp',
                  ].map((logo, index) => renderLogo(logo, `bottom-${index}`, index + 1))}
                  {/* Duplicate set for seamless loop */}
                  {[
                    '/images/brands/veela1.webp',
                    '/images/brands/daphnesandals.webp',
                    '/images/brands/ref8.webp',
                    '/images/brands/ref1.webp',
                    '/images/brands/ref2.webp',
                    '/images/brands/ref3.webp',
                  ].map((logo, index) => renderLogo(logo, `bottom-dup-${index}`, index + 1))}
          </div>
        </div>
                </div>

            {/* Title Below Logos */}
            <div className="text-center mt-4">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gray-50 rounded-full border border-gray-100">
                <TrendingUpIcon className="w-4 h-4 text-gray-400" />
                <p className="text-sm md:text-base text-gray-700 font-medium">
                  {pageContent.home.references.title}
                </p>
          </div>
        </div>
          </motion.div>
        </div>
      </section>

    </div>
  )
}

export default Services

